import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../lib/cart";
import { toast } from "sonner";
import { ShoppingCart, ArrowRight } from "@phosphor-icons/react";

/**
 * Single source of truth for showing purchase controls.
 * A product is purchasable iff:
 *   product.active === true
 *   AND product.ecommerce_enabled === true
 *   AND product.checkout_status === "Add to Cart"
 *   AND product.card_payment_allowed === true
 *   AND product.public_price_visible === true
 *   AND product.retail_price_cents > 0
 * Otherwise: fall back to the "Contact for Availability" surface (dialog handled by parent).
 */
export const isPurchasable = (p) =>
  !!(
    p &&
    p.active &&
    p.ecommerce_enabled &&
    p.checkout_status === "Add to Cart" &&
    p.card_payment_allowed &&
    p.public_price_visible &&
    (p.retail_price_cents || 0) > 0
  );

export const ProductPurchaseActions = ({
  product,
  variant = "card",            // 'card' | 'detail'
  quantity = 1,
  onIncrement,
  onDecrement,
  onOpenRequest,               // detail variant only
  navigate,                    // needed for Buy Now
}) => {
  const { add } = useCart();
  const purchasable = isPurchasable(product);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      await add(product, quantity);
      toast.success(`${product.product_name} added to cart`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add to cart");
    }
  };

  const handleBuyNow = async (e) => {
    e?.preventDefault?.();
    try {
      await add(product, quantity, { openDrawer: false });
      navigate?.("/cart");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add to cart");
    }
  };

  // Compact card variant
  if (variant === "card") {
    if (purchasable) {
      return (
        <button
          onClick={handleAdd}
          data-testid={`add-to-cart-quick-${product.slug}`}
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy2 text-white font-semibold px-4 py-3 text-sm rounded-[12px] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft"
        >
          <ShoppingCart size={14} weight="regular" /> Add to Cart
        </button>
      );
    }
    return (
      <Link
        to={`/product/${product.slug}`}
        data-testid={`view-product-${product.slug}`}
        className="w-full inline-flex items-center justify-center gap-2 border border-brand-border hover:border-brand-navy text-brand-navy px-4 py-3 text-sm font-semibold rounded-[12px] transition-colors duration-200"
      >
        Contact for Availability <ArrowRight size={12} weight="regular" />
      </Link>
    );
  }

  // Detail variant — quantity picker + Add + Buy Now
  if (purchasable) {
    return (
      <div className="mt-6 space-y-3" data-testid="purchase-actions-buyable">
        <div className="flex items-center gap-3">
          <div className="flex items-center border border-brand-border rounded-[12px] bg-white overflow-hidden">
            <button
              onClick={onDecrement}
              data-testid="qty-decrement"
              className="px-4 py-3 text-brand-text hover:bg-brand-surface transition-colors"
              aria-label="Decrease quantity"
            >−</button>
            <span className="px-4 py-3 font-semibold text-brand-navy min-w-[3rem] text-center" data-testid="qty-value">{quantity}</span>
            <button
              onClick={onIncrement}
              data-testid="qty-increment"
              className="px-4 py-3 text-brand-text hover:bg-brand-surface transition-colors"
              aria-label="Increase quantity"
            >+</button>
          </div>
          <button
            onClick={handleAdd}
            data-testid="add-to-cart-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold px-6 py-3.5 text-base rounded-[14px] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft"
          >
            <ShoppingCart size={16} weight="regular" /> Add to Cart
          </button>
        </div>
        <button
          onClick={handleBuyNow}
          data-testid="buy-now-btn"
          className="w-full border border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-6 py-3.5 text-base rounded-[14px] transition-colors duration-200"
        >
          Buy Now
        </button>
      </div>
    );
  }

  // Non-purchasable detail: contact for availability CTA (parent owns dialog)
  return (
    <button
      onClick={onOpenRequest}
      data-testid="request-availability-btn"
      className="mt-6 w-full bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold px-6 py-3.5 text-base rounded-[14px] inline-flex items-center justify-center gap-2 transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft"
    >
      Contact for Availability <ArrowRight size={16} weight="regular" />
    </button>
  );
};

export default ProductPurchaseActions;
